import { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router";
import { useToast } from "../../components/ui/Toast";
import {
  BlogPostEditor,
  blogPostFormValuesFromRecord,
  type BlogPostSavePayload,
} from "./blog-edit/BlogPostEditor";
import {
  DELETE_BLOG_POST,
  UPDATE_BLOG_POST,
} from "./blog-edit/blogPostMutations";
import { useBlogPostData } from "./blog-edit/useBlogPostData";
import { GET_NAV_CONFIG } from "./navigation/queries";
import { parseBlogBasePath } from "./blog-edit/parseBlogBasePath";

interface UpdateResponse {
  updateBlogPost: { id: string; title: string } | null;
}

export function BlogPostEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const { post, loading, error } = useBlogPostData(id);
  const { data: navData } = useQuery(GET_NAV_CONFIG, { fetchPolicy: "cache-first" });
  const blogBasePath = parseBlogBasePath(navData?.currentTenant?.featureConfig);
  const [update, updateResult] = useMutation<UpdateResponse>(UPDATE_BLOG_POST, {
    refetchQueries: ["AllBlogPostsForListing"],
  });
  const [del, delResult] = useMutation(DELETE_BLOG_POST, {
    refetchQueries: ["AllBlogPostsForListing"],
  });
  const [saveError, setSaveError] = useState<string | null>(null);

  const initialValues = useMemo(
    () => (post ? blogPostFormValuesFromRecord(post) : null),
    [post],
  );

  async function handleSave(payload: BlogPostSavePayload) {
    if (!id) return;
    setSaveError(null);
    try {
      const result = await update({
        variables: {
          id,
          title: payload.title,
          slug: payload.slug,
          author: payload.author,
          summary: payload.summary || null,
          body: payload.body || null,
          publishAt: payload.publishAt,
          featuredImageUrl: payload.featuredImageUrl || null,
          featuredImageAlt: payload.featuredImageAlt.trim() || null,
          tagNames: payload.tagNames,
        },
      });
      const updated = result.data?.updateBlogPost;
      if (!updated) throw new Error("Couldn't save post");
      toast.push({
        kind: "success",
        title: `"${updated.title}" saved`,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      setSaveError(message);
      toast.push({
        kind: "error",
        title: "Couldn't save post",
        description: message,
      });
    }
  }

  async function handleDelete() {
    if (!id || !post) return;
    const ok = window.confirm(
      `Delete "${post.title}"? This removes the post and any uploaded images permanently.`,
    );
    if (!ok) return;
    try {
      await del({ variables: { id } });
      toast.push({
        kind: "success",
        title: `"${post.title}" deleted`,
      });
      navigate("/website/blog");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unknown error";
      toast.push({
        kind: "error",
        title: "Couldn't delete post",
        description: message,
      });
    }
  }

  if (loading && !post) {
    return (
      <div className="px-7 py-10 text-[13px] text-ink-4">Loading post…</div>
    );
  }
  if (error || !post || !initialValues) {
    return (
      <div className="px-7 py-10">
        <div className="mb-3 text-[13px] text-rust">
          Couldn't load post: {error?.message ?? "not found"}
        </div>
        <Link
          to="/website/blog"
          className="text-[13px] text-tide hover:underline"
        >
          ← Back to blog
        </Link>
      </div>
    );
  }

  return (
    <BlogPostEditor
      mode="edit"
      initialValues={initialValues}
      originalPublishAt={post.publishAt}
      isSaving={updateResult.loading}
      saveError={saveError}
      blogBasePath={blogBasePath}
      onSave={handleSave}
      onDelete={handleDelete}
      isDeleting={delResult.loading}
    />
  );
}
